import { useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';
import { useOutsidePress } from '@/lib/use-outside-press';

/**
 * Keyboard and dismissal behaviour for the small menus in the app surfaces (a trigger that opens a
 * list of `role="menuitem"` buttons): focus enters the first item on open, arrows/Home/End move,
 * Escape, an item or Tab close it and return focus to the trigger. A press or focus outside closes it
 * and leaves focus where the person put it, as Radix menus do (taking it back would scroll the page
 * back to the trigger). One place so every menu behaves the same; presentation stays with the caller.
 */
export function useMenuBehavior(open: boolean, close: () => void) {
  const trigger = useRef<HTMLButtonElement | null>(null);
  const root = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const closedOutside = useRef(false);

  const visibleItems = () =>
    [...(root.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])].filter(
      (item) =>
        item.getAttribute('aria-disabled') !== 'true' &&
        // a phone-only item is `display: none` on wide screens: it cannot take focus
        getComputedStyle(item).display !== 'none',
    );

  useEffect(() => {
    if (open) {
      visibleItems()[0]?.focus();
    } else if (wasOpen.current && !closedOutside.current) {
      trigger.current?.focus();
    }
    closedOutside.current = false;
    wasOpen.current = open;
  }, [open]);

  useOutsidePress(open, root, () => {
    closedOutside.current = true;
    close();
  });

  const onKeyDown = (event: KeyboardEvent) => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    const items = visibleItems();
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => {
      event.preventDefault();
      items[(to + items.length) % items.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(index + 1);
    else if (event.key === 'ArrowUp') move(index < 0 ? -1 : index - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(items.length - 1);
    else if (event.key === 'Tab') close();
  };

  return {
    trigger: trigger as RefObject<HTMLButtonElement>,
    root: root as RefObject<HTMLElement>,
    onKeyDown,
  };
}
